import { ClickAwayListener, Drawer, PaperProps, Tab, Tabs, IconButton } from '@mui/material';
import { JSX, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { isChildOf } from '../services';
import {
  styleConstants,
  administrationTabs,
  dataMaintenanceTabs,
  fearureFlagTabs,
  productTabs,
  notificationTabs,
  featureTabs,
  EModuleType,
} from '../data/Constants';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { styled } from '@mui/material/styles';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import PersonIcon from '@mui/icons-material/Person';
import KeyIcon from '@mui/icons-material/Key';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import TranslateIcon from '@mui/icons-material/Translate';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import PermDataSettingIcon from '@mui/icons-material/PermDataSetting';
import CategoryIcon from '@mui/icons-material/Category';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import WebIcon from '@mui/icons-material/Web';
import FlagIcon from '@mui/icons-material/Flag';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PostAddIcon from '@mui/icons-material/PostAdd';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import HandymanIcon from '@mui/icons-material/Handyman';
import {
  setForceShowSectionTabs,
  setShowSectionTabs,
} from '../store/states/ApplicationSettingsSlice';
import { AppState } from '../store/AppStore';
import PinIcon from '@mui/icons-material/Pin';
import { Queue, RestoreFromTrash } from '@mui/icons-material';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import LinkIcon from '@mui/icons-material/Link';

import type { ITabDrawerProps, IApplicationSettings, ISectionTab } from '../../types';

const getSXPaperProps = (applicationSettings: IApplicationSettings) => {
  return {
    position: applicationSettings.pageSize.isSmall ? 'absolute' : 'initial',
    transition: '.15s',
    boxShadow: applicationSettings.pageSize.isSmall ? '8px 0 8px -8px black' : 'none',
    top: applicationSettings.pageSize.isExtraSmall
      ? styleConstants.extraSmallHeaderHeight
      : styleConstants.headerHeight,
    bottom: '0',
    height: applicationSettings.pageSize.isSmall ? 'unset' : '100%',
  };
};

function showTab(applicationSettings: IApplicationSettings) {
  return (
    (!applicationSettings.pageSize.isSmall && applicationSettings.showSectionTabs) ||
    (applicationSettings.pageSize.isSmall && applicationSettings.forceShowSectionTabs)
  );
}

/**
 * Icon mapping for different module types and indices
 */
const iconMapping: Record<string, Array<{ key: string; icon: JSX.Element }>> = {
  [EModuleType.Administration]: [
    { key: 'User', icon: <PersonIcon /> },
    { key: 'Role', icon: <ManageAccountsIcon /> },
    { key: 'Apikey', icon: <KeyIcon /> },
    { key: 'Application', icon: <WebIcon /> },
  ],
  [EModuleType.Datamaintainance]: [
    { key: 'Sales', icon: <NotesOutlinedIcon /> },
    { key: 'Image', icon: <ImageOutlinedIcon /> },
    { key: 'Product', icon: <AccountTreeOutlinedIcon /> },
    { key: 'Price', icon: <AttachMoneyIcon /> },
    { key: 'Pricelist', icon: <RequestQuoteIcon /> },
    { key: 'TechnicalInfo', icon: <HandymanIcon /> },
  ],
  [EModuleType.Product]: [
    { key: 'Market', icon: <StorefrontIcon /> },
    { key: 'MarketAssociation', icon: <AddBusinessIcon /> },
    { key: 'Language', icon: <TranslateIcon /> },
    { key: 'LanguageAssociation', icon: <LanguageIcon /> },
    { key: 'ProductCatalog', icon: <CategoryIcon /> },
    { key: 'InternalProductCatalog', icon: <CategoryOutlinedIcon /> },
    { key: 'ProductConfig', icon: <PermDataSettingIcon /> },
    { key: 'Archive', icon: <RestoreFromTrash /> },
    { key: 'Properties', icon: <PostAddIcon /> },
    { key: 'PropertyAssociation', icon: <DynamicFeedIcon /> },
  ],
  [EModuleType.Notifications]: [
    { key: 'Ace', icon: <NotificationsIcon /> },
    { key: 'Queue', icon: <Queue /> },
    { key: 'ProductRelease', icon: <NewReleasesIcon /> },
    { key: 'ProductVersionMapping', icon: <LinkIcon /> },
  ],
  [EModuleType.FeatureFlags]: [{ key: 'Flag', icon: <FlagIcon /> }],
  [EModuleType.Feature]: [{ key: 'Feature', icon: <PinIcon /> }],
};

/**
 * This method returns the icon for the tabs
 * @param {moduleType} moduleType type of module
 * @param {key} key key of the icon
 */
export function getIcon(moduleType: string, key: string): JSX.Element | null {
  const icons = iconMapping[moduleType];
  if (!icons) return null;
  const found = icons.find((item) => item.key === key);
  return found ? found.icon : null;
}

/**
 * Renders the section tabs on the left side on the home screen.
 * Gets automatically hidden on smaller screens
 * @param {ITabDrawerProps} props the properties for the tab drawer component
 * @returns {JSX.Element} the tab drawer component
 */
export const TabDrawer = ({ tabValue }: ITabDrawerProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const applicationSettings = useSelector((state: AppState) => state.applicationSettings);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const path = globalThis.location.pathname.split('/')[1];
    const index = path ? sections.findIndex((s) => s.path === '/' + path) : 0;
    setActiveTab(Math.max(index, 0));
  }, [location.pathname]);

  if (!applicationSettings || !tabValue) {
    return null;
  }

  let sections: ISectionTab[];
  switch (tabValue) {
    case EModuleType.Administration:
      sections = administrationTabs;
      break;
    case EModuleType.Datamaintainance:
      sections = dataMaintenanceTabs;
      break;
    case EModuleType.Product:
      sections = productTabs;
      break;
    case EModuleType.FeatureFlags:
      sections = fearureFlagTabs;
      break;
    case EModuleType.Notifications:
      sections = notificationTabs;
      break;
    case EModuleType.Feature:
      sections = featureTabs;
      break;
  }

  const onTabChange = (_: React.SyntheticEvent<Element, Event>, value: number) => {
    navigate(sections[value].path);
    setActiveTab(value);
  };

  const onClickAway = (e: MouseEvent | TouchEvent) => {
    if (
      !isChildOf(e.target as HTMLElement, 'tabDrawerToggle') &&
      applicationSettings.forceShowSectionTabs
    ) {
      dispatch(setForceShowSectionTabs(false));
    }
  };

  const show = showTab(applicationSettings);
  const paperProps: Partial<PaperProps<'div', unknown>> = {
    sx: getSXPaperProps(applicationSettings),
    className: classNames('w-0', { ['drawerOpen']: show }),
  };
  let tabIndex = 0;
  let minitabIndex = 0;

  const tabs = sections?.map((s) => (
    <Tab
      key={`${s.id}-${tabIndex}`}
      id={s.id}
      label={<span>{s.name}</span>}
      value={tabIndex++}
      className="tab-drawer-tabs"
      iconPosition="start"
      icon={getIcon(tabValue, s.key)}
    />
  ));
  const minitabs = sections?.map((s) => (
    <Tab
      key={`${s.id}-${minitabIndex}`}
      id={s.id}
      value={minitabIndex++}
      className="tab-drawer-tabs"
      icon={getIcon(tabValue, s.key)}
    />
  ));

  const DrawerHeader = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
  }));

  const onToggleDrawer = () => {
    if (applicationSettings.pageSize.isSmall) {
      dispatch(setForceShowSectionTabs(!applicationSettings.forceShowSectionTabs));
    } else {
      dispatch(setShowSectionTabs(!applicationSettings.showSectionTabs));
    }
  };

  return (
    <>
      {show ? (
        <ClickAwayListener onClickAway={onClickAway}>
          <Drawer
            variant="permanent"
            open
            className="h-100"
            slotProps={{
              paper: paperProps,
            }}
          >
            <DrawerHeader>
              <IconButton onClick={onToggleDrawer} className="left-arrow">
                <KeyboardDoubleArrowLeftIcon className={'pinIcon'} />
              </IconButton>
            </DrawerHeader>
            <Tabs
              value={activeTab}
              onChange={onTabChange}
              orientation="vertical"
              variant="scrollable"
              scrollButtons={false}
              visibleScrollbar
              className="tab-toggle"
            >
              {tabs}
            </Tabs>
          </Drawer>
        </ClickAwayListener>
      ) : (
        <div className="tabDrawerMini">
          <IconButton id="tabDrawerToggle" onClick={onToggleDrawer} className="toggleIcon">
            <KeyboardDoubleArrowRightIcon className={'pinIcon'} />
          </IconButton>

          <Tabs
            value={activeTab}
            onChange={onTabChange}
            orientation="vertical"
            variant="scrollable"
            scrollButtons={false}
            visibleScrollbar
            className="tab-toggle"
          >
            {minitabs}
          </Tabs>
        </div>
      )}
    </>
  );
};
